import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CheckBox, SelectInput } from "../../../../../../design-system/controls";
import { CheckBoxVariant } from "../../../../../../design-system/controls/checkBox/CheckBox";
import type { SelectOption } from "../../../../../../design-system/controls/SelectInput/SelectInput";
import useRemoteCollectors from "../../../../../RemoteCollectors/useRemoteCollectors";
import {
  isUndefinedOrNull,
  REQUIRED_ERROR_MESSAGE,
} from "../../../../../../utils";

export interface RemoteCollectorSettingProps {
  rapId?: number | string;
  disable?: boolean;
}

const RemoteCollectorSetting: React.FC<RemoteCollectorSettingProps> = ({
  rapId,
  disable,
}) => {
  const { mappedRapInstances, loading } = useRemoteCollectors();
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const isRemoteCollector = useWatch({
    control,
    name: `isRemoteCollector`,
    defaultValue: !isUndefinedOrNull(rapId),
  });

  return (
    <>
      <Controller
        name={`isRemoteCollector`}
        defaultValue={!isUndefinedOrNull(rapId)}
        render={({ field: { value, onChange } }) => {
          return (
            <CheckBox
              data-testid={"is-remote-collector"}
              variant={CheckBoxVariant.TOGGLE}
              onChange={(val, newValue) => {
                onChange(newValue);
              }}
              checked={value}
              label={t("Remote Collector")}
              disabled={disable || !mappedRapInstances?.length}
              withHelp={t(
                "Remote Collector enables you to send Findings from your internal 3rd party integrations to Seemplicity without giving access to your network."
              )}
              isLoading={loading}
            />
          );
        }}
        control={control}
      />
      {isRemoteCollector && !!mappedRapInstances && (
        <Controller
          shouldUnregister
          name={`selectedRapInstance`}
          defaultValue={
            rapId
              ? mappedRapInstances.find((instance) => instance.value === rapId)
              : mappedRapInstances[0]
          }
          render={({ field: { value, onChange } }) => (
            <SelectInput
              data-testid={"remote-collector-dropdown"}
              name={"remoteCollectorDropdown"}
              value={value}
              labelTop
              onChange={(selectedValue) => onChange(selectedValue)}
              error={
                errors?.selectedRapInstance ? errors.selectedRapInstance : null
              }
              label={t("Remote Collector")}
              // mappedRapInstances.label derives from instance.name (optionally undefined); option labels are tolerated at runtime
              options={mappedRapInstances as SelectOption[]}
              required
              isLoading={loading}
            />
          )}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
      )}
    </>
  );
};
export default RemoteCollectorSetting;
